import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import React, { FC } from "react";
import CloseIcon from "@mui/icons-material/Close";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import "./downtimeEventInfoDialog.css";
import { IUptimeHistoryDetails, IUptimeRowDetails } from "../../../types";
import { DataGrid, GridCellParams, GridColDef, GridValueFormatterParams } from "@mui/x-data-grid";
import { PAGINATION_CONSTANTS } from "../../../utils/constants";
import moment from "moment";
import { getFormattedDuration } from "../../../utils/commonUtils";

interface IDowntimeEventInfoDialogProps {
	open: boolean;
	eventDetails: IUptimeRowDetails | null;
	onClose: () => void;
}

const TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const DowntimeEventInfoDialog: FC<IDowntimeEventInfoDialogProps> = (props) => {
	const { open, eventDetails, onClose } = props;

	const columnsData: GridColDef[] = [
		{
			field: "status",
			headerName: "Device Status",
			minWidth: 150,
			flex: 2,
			sortable: false,
			align: "center",
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IUptimeHistoryDetails>) => params.row.status,
			valueFormatter: (params: GridValueFormatterParams<IUptimeHistoryDetails["status"]>) =>
				params.value === "Online" ? "Device Online" : params.value === "Offline" ? "Device Offline" : ""
		},
		{
			field: "from",
			headerName: "From",
			minWidth: 120,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (params) => moment(params.value, TIME_FORMAT).format("hh:mm A"),
			cellClassName: (params: GridCellParams<IUptimeHistoryDetails>) => params.row.status
		},
		{
			field: "to",
			headerName: "To",
			minWidth: 120,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (params) => moment(params.value, TIME_FORMAT).format("hh:mm A"),
			cellClassName: (params: GridCellParams<IUptimeHistoryDetails>) => params.row.status
		},
		{
			field: "duration",
			headerName: "Duration",
			minWidth: 150,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (params) => getFormattedDuration(Number(params.value)),
			cellClassName: (params: GridCellParams<IUptimeHistoryDetails>) => params.row.status
		}
	];

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "#5B4EBF",
					color: "white",
					position: "relative",
					padding: { xs: "0.75rem 1rem 0.5rem 1rem", sm: "16px 20px 12px 20px" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Typography
					variant="h6"
					sx={{
						fontWeight: 600,
						fontSize: { xs: "1.125rem", sm: "1.25rem" }
					}}
				>
					Downtime Event Information
				</Typography>
				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				sx={{
					padding: { xs: "1rem !important", sm: "20px !important" },
					background: "#ffffff",
					overflowY: "auto"
				}}
			>
				{eventDetails ? (
					<Box className="downtime-event-info-dialog-content-wrapper">
						<Box className="event-details-date-wrapper">
							<Box className="date-icon-wrapper">
								<CalendarTodayIcon />
							</Box>

							<Typography
								sx={{
									fontWeight: 600,
									fontSize: { xs: "1rem", sm: "1.125rem" },
									color: "#2D2D2D"
								}}
							>
								{moment(eventDetails.date).format("DD/MM/YYYY")}
							</Typography>
						</Box>

						<Box className="event-details-wrapper">
							<Box className="event-details-card success">
								<Box className="event-details-card-icon">
									<TimelapseIcon color="inherit" />
								</Box>

								<Box className="event-details-card-text">
									<Typography
										variant="body2"
										fontWeight={500}
										color="#666"
										sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
									>
										Total Uptime Duration
									</Typography>

									<Typography fontWeight={600} color="#2D2D2D" sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
										{eventDetails.uptime}
									</Typography>
								</Box>
							</Box>

							<Box className="event-details-card error">
								<Box className="event-details-card-icon">
									<TimelapseIcon color="inherit" />
								</Box>

								<Box className="event-details-card-text">
									<Typography
										variant="body2"
										fontWeight={500}
										color="#666"
										sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
									>
										Total Downtime Duration
									</Typography>

									<Typography fontWeight={600} color="#2D2D2D" sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}>
										{eventDetails.downtime}
									</Typography>
								</Box>
							</Box>
						</Box>

						<Box className="event-history-table-wrapper">
							<DataGrid
								columns={columnsData}
								rows={eventDetails.history.map((item, index) => ({ ...item, id: index }))}
								disableColumnMenu
								disableRowSelectionOnClick
								density="compact"
								rowSpacingType="border"
								columnHeaderHeight={80}
								pageSizeOptions={[PAGINATION_CONSTANTS.ROWS_PER_PAGE]}
								slots={{
									footer: () => <></>
								}}
								classes={{
									root: "downtime-events-table-root",
									columnHeader: "downtime-events-table-column-header",
									columnSeparator: "downtime-events-table-column-separator",
									columnHeaders: "downtime-events-table-column-headers",
									cell: "downtime-events-table-cell"
								}}
							/>
						</Box>
					</Box>
				) : null}
			</DialogContent>
		</Dialog>
	);
};

export default DowntimeEventInfoDialog;
